'use client';

import { useState } from 'react';

/**
 * NestedTabs — vertical category tabs (left) + FAQ accordion (right).
 * Used by privacy-policy and help-center via the `nested-tabs` archetype.
 *
 * Each tab corresponds to a category. Each question/answer pair under a tab
 * is a <details> accordion. First tab is selected on mount; clicking another
 * swaps the right-side panel.
 *
 * If incoming sections have no H3 items, the H3-less H2s are turned into
 * accordion items themselves (so flat content still renders nicely).
 */
import { useTranslation } from "@/hooks/useTranslation";
export default function NestedTabs({
  tabs
}) {
  const {
    t
  } = useTranslation();
  const [active, setActive] = useState(0);
  if (!tabs?.length) return null;
  const current = tabs[active] || tabs[0];
  return <div className="lb-tabs-shell">
      <nav className="lb-tabs-nav" role="tablist" aria-orientation="vertical">
        {tabs.map((t, i) => <button key={i} role="tab" aria-selected={i === active} className="lb-tab-btn" onClick={() => setActive(i)}>
            <TabIcon index={i} />
            <span>{t.heading}</span>
          </button>)}
      </nav>

      <div role="tabpanel">
        {current.intro && <div className="lb-archetype-section__intro" dangerouslySetInnerHTML={{
        __html: current.intro
      }} />}
        {current.items.length > 0 ? <div className="lb-faq">
            {current.items.map((it, j) => <details key={j} className="lb-faq__item" open={j === 0}>
                <summary className="lb-faq__summary">{it.heading}</summary>
                <div className="lb-faq__body" dangerouslySetInnerHTML={{
            __html: it.html
          }} />
              </details>)}
          </div> : <div className="lb-prose-empty">{t("tabs.no_entries_in_this_category_yet")}</div>}
      </div>
    </div>;
}

/* Tiny inline SVG icons — one per tab index, cycles if more tabs than icons. */
const ICON_PATHS = [
// shield
<path key="p" d="M12 2l8 4v6c0 5-3.5 9-8 10-4.5-1-8-5-8-10V6l8-4z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />,
// cloud
<path key="p" d="M17 18H8a5 5 0 010-10 6 6 0 0111.5 1.5A3.5 3.5 0 0117 18z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />,
// lock
<g key="g" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"><rect x="5" y="10" width="14" height="10" rx="2" /><path d="M8 10V7a4 4 0 018 0v3" /></g>,
// share / git-fork
<g key="g" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="6" cy="6" r="2" /><circle cx="18" cy="6" r="2" /><circle cx="12" cy="18" r="2" /><path d="M6 8v3a2 2 0 002 2h8a2 2 0 002-2V8M12 14v2" /></g>,
// sparkle / AI
<path key="p" d="M12 3l2 5 5 2-5 2-2 5-2-5-5-2 5-2 2-5z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />,
// users
<g key="g" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"><circle cx="9" cy="9" r="3" /><path d="M3 19a6 6 0 0112 0M16 11a3 3 0 100-6M14 19a5 5 0 016-5" /></g>,
// refresh
<path key="p" d="M3 12a9 9 0 0115-6.7M21 12a9 9 0 01-15 6.7M21 3v6h-6M3 21v-6h6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />,
// book
<path key="p" d="M4 5a2 2 0 012-2h12v18H6a2 2 0 01-2-2V5zM4 19a2 2 0 012-2h12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />];
function TabIcon({
  index
}) {
  const {
    t
  } = useTranslation();
  const icon = ICON_PATHS[index % ICON_PATHS.length];
  return <svg className="lb-tab-btn__icon" viewBox="0 0 24 24">{icon}</svg>;
}